import React, { ReactNode, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Home, Grid3x3, Map, MessageCircle, User, Settings,
  Bell, Zap, Moon, Sun, Search, X,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { PWAInstallButton } from '@/components/common/PWAInstallButton';

interface MainLayoutProps {
  children: ReactNode;
}

const navLinks = [
  { name: 'Home', path: '/',     icon: Home },
  { name: 'Feed', path: '/feed', icon: Grid3x3 },
  { name: 'Map',  path: '/map',  icon: Map },
  { name: 'Chat', path: '/chat', icon: MessageCircle },
];

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, isAuthenticated } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const isFeed = location.pathname === '/feed';

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) navigate(`/feed?q=${encodeURIComponent(searchQuery.trim())}`);
  };

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg flex flex-col">

      {/* ── Top Header ── */}
      <header className="sticky top-0 z-50 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border shadow-sm">
        <div className="flex items-center gap-3 px-4 h-14">

          {/* Brand */}
          <NavLink to="/" className="flex items-center gap-2 flex-shrink-0 mr-2">
            <div className="w-8 h-8 rounded-xl bg-primary flex items-center justify-center">
              <Zap size={16} className="text-white" />
            </div>
            <span className="hidden sm:block text-base font-bold text-light-text dark:text-dark-text tracking-tight">
              Majengo
            </span>
          </NavLink>

          {/* Desktop Nav Links */}
          <nav className="hidden md:flex items-center gap-1 flex-1">
            {navLinks.map((tab) => (
              <NavLink
                key={tab.path}
                to={tab.path}
                end={tab.path === '/'}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all',
                    isActive
                      ? 'bg-primary/10 text-primary dark:text-golden'
                      : 'text-light-subtle dark:text-dark-subtle hover:bg-light-bg dark:hover:bg-dark-bg hover:text-light-text dark:hover:text-dark-text'
                  )
                }
              >
                <tab.icon size={17} />
                {tab.name}
              </NavLink>
            ))}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-1 ml-auto">

            {/* Feed Search Bar — sits at the start of the right action group */}
            {isFeed && (
              <form onSubmit={handleSearch} className="hidden md:flex items-center mr-1">
                <div className="relative">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle pointer-events-none" />
                  <input
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search posts…"
                    className="w-48 pl-9 pr-8 py-1.5 rounded-full text-sm bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-light-text dark:text-dark-text placeholder-light-subtle dark:placeholder-dark-subtle focus:outline-none focus:ring-2 focus:ring-primary/30 focus:w-64 transition-all duration-200"
                  />
                  {searchQuery && (
                    <button type="button" onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2">
                      <X size={13} className="text-light-subtle dark:text-dark-subtle" />
                    </button>
                  )}
                </div>
              </form>
            )}

            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
            >
              {isDarkMode
                ? <Moon size={18} className="text-light-subtle dark:text-dark-subtle" />
                : <Sun size={18} className="text-light-subtle dark:text-dark-subtle" />
              }
            </button>

            {/* Notifications */}
            {isAuthenticated && (
              <button
                onClick={() => navigate('/notifications')}
                className="p-2 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors relative"
              >
                <Bell size={20} className="text-light-subtle dark:text-dark-subtle" />
              </button>
            )}

            {/* Settings */}
            {isAuthenticated && (
              <NavLink
                to="/settings"
                className={({ isActive }) =>
                  cn('p-2 rounded-lg transition-colors',
                    isActive
                      ? 'bg-primary/10 text-primary dark:text-golden'
                      : 'hover:bg-light-bg dark:hover:bg-dark-bg text-light-subtle dark:text-dark-subtle'
                  )
                }
              >
                <Settings size={20} />
              </NavLink>
            )}

            {/* PWA Install Button */}
            <PWAInstallButton variant="primary" className="hidden md:flex ml-2" />

            {/* Profile / Login */}
            <NavLink
              to={isAuthenticated ? '/profile' : '/login'}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
                  isActive
                    ? 'bg-primary/10 text-primary dark:text-golden'
                    : 'hover:bg-light-bg dark:hover:bg-dark-bg text-light-subtle dark:text-dark-subtle'
                )
              }
            >
              {isAuthenticated && user ? (
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center">
                    <span className="text-white text-xs font-bold">
                      {profile?.full_name?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                  <span className="hidden sm:block text-sm font-medium text-light-text dark:text-dark-text">
                    {profile?.full_name?.split(' ')[0]}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <User size={18} />
                  <span className="hidden sm:block">Sign In</span>
                </div>
              )}
            </NavLink>
          </div>
        </div>
      </header>

      {/* ── Main Content ── */}
      <main className="flex-1 w-full">
        {children}
      </main>

      {/* ── Mobile Bottom Nav ── */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 h-16 bg-light-card dark:bg-dark-card border-t border-light-border dark:border-dark-border">
        <div className="flex h-full items-center justify-around px-2">
          {navLinks.map((tab) => (
            <NavLink
              key={tab.path}
              to={tab.path}
              end={tab.path === '/'}
              className={({ isActive }) =>
                cn(
                  'flex flex-col items-center justify-center flex-1 text-xs transition-colors gap-0.5',
                  isActive
                    ? 'text-primary dark:text-golden'
                    : 'text-light-subtle dark:text-dark-subtle'
                )
              }
            >
              {({ isActive }) => (
                <>
                  <tab.icon size={21} className={isActive ? 'stroke-[2.5px]' : ''} />
                  <span className="text-[10px] font-medium">{tab.name}</span>
                </>
              )}
            </NavLink>
          ))}
          <NavLink
            to={isAuthenticated ? '/profile' : '/login'}
            className={({ isActive }) =>
              cn(
                'flex flex-col items-center justify-center flex-1 text-xs transition-colors gap-0.5',
                isActive ? 'text-primary dark:text-golden' : 'text-light-subtle dark:text-dark-subtle'
              )
            }
          >
            {({ isActive }) => (
              <>
                <User size={21} className={isActive ? 'stroke-[2.5px]' : ''} />
                <span className="text-[10px] font-medium">Profile</span>
              </>
            )}
          </NavLink>
        </div>
      </nav>
    </div>
  );
};
